import React from "react";

import type { AnnotationMarkerReadModel } from "./annotationMarker.types";

const TICKER_STYLE_ID = "annotation-marker-ticker-keyframes";

function ensureTickerStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById(TICKER_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = TICKER_STYLE_ID;
  style.textContent = `
    @keyframes annotation-marker-ticker {
      0% { transform: translate3d(0, 0, 0); }
      100% { transform: translate3d(-50%, 0, 0); }
    }
  `;
  document.head.appendChild(style);
}

export default function AnnotationMarkerBadge({ marker }: { marker: AnnotationMarkerReadModel }): JSX.Element | null {
  if (!marker.showBadge) return null;

  ensureTickerStyle();

  const severityLabel = marker.severity ? marker.severity.slice(0, 4).toUpperCase() : null;
  const tickerText = marker.tickerText ?? marker.fullLabel;

  return (
    <div
      className="pointer-events-none absolute left-1/2 top-0"
      style={{ transform: "translate(-50%, -45%)" }}
    >
      <div
        className="flex items-center gap-1 rounded-full border px-2 py-[3px]"
        style={{
          borderColor: marker.isSelected ? "rgba(255,255,255,0.78)" : marker.palette.border,
          background: marker.palette.badgeBg,
          color: marker.palette.text,
          boxShadow: marker.isFresh ? `0 0 14px ${marker.palette.glow}` : "0 4px 14px rgba(0,0,0,0.22)",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.08em",
          lineHeight: 1,
          maxWidth: marker.showLabel ? 208 : undefined,
        }}
      >
        <span>{marker.shortLabel}</span>
        {severityLabel ? (
          <span className="rounded-full border border-white/12 bg-black/15 px-1.5 py-[2px] text-[9px] tracking-[0.12em]">
            {severityLabel}
          </span>
        ) : null}
        {marker.showLabel ? (
          marker.shouldTicker ? (
            <span
              className="relative block overflow-hidden whitespace-nowrap"
              style={{ width: 136, maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)" }}
            >
              <span
                className="inline-flex min-w-max items-center gap-6 whitespace-nowrap"
                style={{ animation: "annotation-marker-ticker 10s linear infinite" }}
              >
                <span>{tickerText}</span>
                <span aria-hidden="true">{tickerText}</span>
              </span>
            </span>
          ) : (
            <span className="max-w-[136px] truncate tracking-[0.05em]">{tickerText}</span>
          )
        ) : null}
      </div>
    </div>
  );
}